import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Clock, History, PackageMinus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import apiClient from '@/services/apiClient';
import { DeliveryList } from './DeliveryList';
import { ReturnForm } from './ReturnForm';
import { ReturnsList } from './ReturnsList';

const TABS = ['new', 'pending', 'history'];
const LABEL = { greige: 'Greige', fob: 'FOB' };

const NewReturn = ({ section, refreshKey, onRaised }) => {
  const [delivery, setDelivery] = useState(null);
  const pick = (d) => {
    setDelivery(d);
    if (window.innerWidth < 1024) setTimeout(() => document.getElementById('sr-form')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <DeliveryList section={section} selectedId={delivery?.entry_id} onPick={pick} refreshKey={refreshKey} />
      <section className="space-y-3 min-w-0" id="sr-form">
        <h2 className="text-sm font-semibold text-[#1A1F2E]">2. Type the pcs going back and why</h2>
        {!delivery ? <p className="text-sm text-gray-500 rounded-lg border border-dashed border-[#E5DFD0] p-6 text-center" data-testid="sr-pick-hint">Pick a delivery first.</p>
          : <ReturnForm section={section} entryId={delivery.entry_id} refreshKey={refreshKey} onRaised={onRaised} />}
      </section>
    </div>
  );
};

/** Supplier Returns (Greige / FOB) — return goods received without a PO or above the PO. */
export default function SupplierReturns() {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'new';
  const [refresh, setRefresh] = useState(0);
  const [pending, setPending] = useState(null);
  const label = LABEL[sectionId];

  useEffect(() => {
    if (!label) return;
    apiClient.get('/supplier-returns', { params: { section: sectionId, status: 'PENDING' } })
      .then((r) => setPending(r.data.counts?.PENDING || 0)).catch(() => {});
  }, [sectionId, label, refresh]);

  if (!label) return <Navigate to="/dashboard" replace />;
  const setTab = (v) => setParams({ tab: v }, { replace: true });
  const changed = () => setRefresh((k) => k + 1);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="supplier-returns-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 min-h-16 py-2 md:py-0 md:h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/sections/${sectionId}`)} className="text-white hover:bg-white/10" data-testid="sr-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex-1 min-w-0 pt-6 md:pt-0">
            <h1 className="text-lg md:text-xl font-bold flex items-center gap-2"><PackageMinus className="w-5 h-5 text-amber-300" />Supplier Returns</h1>
            <p className="text-xs md:text-sm text-gray-400 truncate">{label} · send back goods received without a PO or above the PO</p>
          </div>
          <Button variant="ghost" size="sm" onClick={changed} className="text-white hover:bg-white/10" data-testid="sr-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-5 flex-wrap h-auto">
            <TabsTrigger value="new" data-testid="sr-tab-new"><PackageMinus className="w-4 h-4 mr-1.5" />New Return</TabsTrigger>
            <TabsTrigger value="pending" data-testid="sr-tab-pending"><Clock className="w-4 h-4 mr-1.5" />Pending{pending ? ` (${pending})` : ''}</TabsTrigger>
            <TabsTrigger value="history" data-testid="sr-tab-history"><History className="w-4 h-4 mr-1.5" />History</TabsTrigger>
          </TabsList>
          <TabsContent value="new"><NewReturn section={sectionId} refreshKey={refresh} onRaised={() => { changed(); setTab('pending'); }} /></TabsContent>
          <TabsContent value="pending"><ReturnsList section={sectionId} mode="pending" refreshKey={refresh} onChanged={changed} /></TabsContent>
          <TabsContent value="history"><ReturnsList section={sectionId} mode="history" refreshKey={refresh} onChanged={changed} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
